import Tracker from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';

import env from '@/config/env';
import { isLocalhostHostname } from '@/utils/hostname';

function isSecureIngestPoint(ingestPoint: string): boolean {
  return ingestPoint.startsWith('https://');
}

const ingestPoint = env.openReplay.ingestPoint;
const isLocalApp =
  typeof window !== 'undefined' &&
  isLocalhostHostname(window.location.hostname);
const useLocalhostWorkaround = env.isLocal() || isLocalApp;
const secureMode = !useLocalhostWorkaround && isSecureIngestPoint(ingestPoint);

function shouldSilenceLocalOpenReplayError(error: unknown): boolean {
  if (!isLocalApp) {
    return false;
  }

  const message = error instanceof Error ? error.message : String(error ?? '');

  return (
    message.includes("Browser doesn't support required api") ||
    message.includes('doNotTrack is active')
  );
}

/**
 * Builds the OpenReplay tracker, or returns null if it cannot be constructed.
 *
 * This is a factory (not a top-level `new Tracker(...)`) for two reasons:
 *   1. main.tsx imports this module before the React app bootstraps, so a
 *      constructor throw at import time would kill app boot. We swallow it.
 *   2. The empty-key guard keeps the tracker disabled when no project key is
 *      configured (local dev, preview builds, etc.).
 * Nothing in this module is allowed to throw synchronously at import time.
 */
function createTracker(): Tracker | null {
  if (!env.openReplay.projectKey || !ingestPoint) {
    return null;
  }

  try {
    return new Tracker({
      projectKey: env.openReplay.projectKey,
      ingestPoint,
      // OpenReplay's documented localhost workaround requires secure-mode checks
      // to be disabled even when sending data to a hosted HTTPS ingest endpoint.
      __DISABLE_SECURE_MODE: !secureMode,
      // Local replay works best when styles are captured aggressively.
      // We raise the backend beacon size limit instead of trimming CSS.
      inlineCss: useLocalhostWorkaround ? 3 : undefined,
      // PHI/PII privacy (styleguide Rule 21): HeartPlus handles protected
      // health information, so sensitive data is masked in-browser before it
      // ever reaches OpenReplay. defaultInputMode=1 (InputMode.Obscured) masks
      // ALL input values — patient names, MRNs, clinical notes, search terms —
      // not just numbers/emails. The obscure* flags additionally scrub PHI that
      // appears in rendered (non-input) text such as DOB, IDs, and emails.
      defaultInputMode: 1,
      obscureInputNumbers: true,
      obscureInputEmails: true,
      obscureInputDates: true,
      obscureTextNumbers: true,
      obscureTextEmails: true,
      // Network capture: never record request/response bodies or headers. They
      // routinely carry PHI (patient payloads from the BFF) and bearer/auth
      // tokens (Keycloak). capturePayload=false drops bodies, ignoreHeaders=true
      // drops all headers, and the sanitizer returning null is belt-and-suspenders
      // so nothing is ever beaconed even if the above defaults change upstream.
      network: {
        capturePayload: false,
        ignoreHeaders: true,
        sanitizer: () => null,
        failuresOnly: false,
        // No session-token header injection: we never correlate captured
        // requests with auth tokens, keeping bearer/Keycloak tokens out of OR.
        sessionTokenHeader: false,
        // Don't reach into cross-origin iframes (e.g. embedded EGM/PDF viewers)
        // whose requests may carry PHI we can't sanitize from here.
        captureInIframes: false,
      },
    });
  } catch (err) {
    // A constructor throw must never break app boot — degrade to "no tracking".
    console.warn('[OpenReplay] Failed to construct tracker:', err);
    return null;
  }
}

const tracker: Tracker | null = createTracker();

export function initOpenReplay(): void {
  if (!tracker) {
    console.debug(
      '[OpenReplay] Skipped: VITE_OPENREPLAY_PROJECT_KEY or VITE_OPENREPLAY_INGEST_POINT not set.',
    );
    return;
  }

  // tracker-assist is disabled for localhost testing because the secure-mode
  // workaround uses insecure transport semantics that don't match hosted assist.
  if (secureMode) {
    tracker.use(trackerAssist());
  }

  console.debug('[OpenReplay] Config:', {
    ingestPoint,
    secureMode,
    appEnv: env.env,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'n/a',
    useLocalhostWorkaround,
  });

  tracker.start().then(
    () => console.debug('[OpenReplay] Session started successfully.'),
    (err) => {
      if (shouldSilenceLocalOpenReplayError(err)) {
        console.debug(
          '[OpenReplay] Skipped for local browser settings or unsupported APIs.',
          err,
        );
        return;
      }

      console.warn('[OpenReplay] Failed to start session:', err);
    },
  );
}

export interface OpenReplayUserMetadata {
  // Clinician (doctor) email — non-patient staff data used to filter sessions
  // by user in the OpenReplay dashboard.
  email?: string;
  practitionerId?: string;
  organizationId?: string;
  [key: string]: string | undefined;
}

export function identifyUser(
  userId: string,
  metadata?: OpenReplayUserMetadata,
): void {
  if (!tracker || !userId) return;

  // Defense in depth: identification runs from a React effect; a throw here
  // (e.g. tracker internals) must never bubble into render.
  try {
    tracker.identify(userId);

    if (!metadata) {
      return;
    }

    for (const [key, value] of Object.entries(metadata)) {
      if (typeof value === 'string' && value.length > 0) {
        tracker.setMetadata(key, value);
      }
    }
  } catch (err) {
    console.warn('[OpenReplay] Failed to identify user:', err);
  }
}

export { createTracker, isSecureIngestPoint, tracker };
